import { createApi } from "@reduxjs/toolkit/query/react";
import { Permission, AccessItem } from "@dexibit/core";
import { createServiceBaseQuery } from "../../../../../store/client/serviceBaseQuery";
import { DexibitUIMessage } from "./types";

export interface Conversation {
  id: string;
  accountId: number;
  parentId?: string;
  forkedFromMessageId?: string;
  compactionSummary?: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  messages?: DexibitUIMessage[];
  isUnsaved?: boolean;
  shared?: boolean;
  ownerId?: number | null;
}

export interface CreateConversationDTO {
  title?: string;
  parentId?: string;
  forkedFromMessageId?: string;
}

export interface UpdateConversationDTO {
  title?: string;
}

export interface GenerateTitleDTO {
  initialMessage: string;
}

export interface BulkAccessRequest {
  all: boolean;
  allPermission?: Permission;
  access: AccessItem[];
}

export interface AccessResponse {
  all: boolean;
  allPermission?: Permission;
  access: AccessItem[];
}

export interface MessageFeedbackRequest {
  messageId: string;
  value: 0 | 1; // 0 = dislike, 1 = like
  comment?: string; // Optional user comment
}

export interface MessageFeedbackResponse {
  success: boolean;
  message: string;
}

export interface ForkConversationDTO {
  messageId: string;
  title?: string;
}

export interface CompactConversationDTO {
  messageId?: string;
}

export const conversationService = createApi({
  reducerPath: "conversationService",
  baseQuery: createServiceBaseQuery("zeusUri"),
  tagTypes: ["Conversations", "Messages", "ConversationAccess", "ConversationSummary"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getConversations: builder.query<Conversation[], void>({
      query: () => "/api/conversations",
      providesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    getConversationById: builder.query<Conversation, { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
      ],
    }),

    createConversation: builder.mutation<Conversation, CreateConversationDTO>({
      query: (conversationData) => ({
        url: "/api/conversations",
        method: "POST",
        body: conversationData,
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    updateConversation: builder.mutation<
      Conversation,
      { conversationId: string; updates: UpdateConversationDTO }
    >({
      query: ({ conversationId, updates }) => ({
        url: `/api/conversations/${conversationId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    updateConversationTitle: builder.mutation<
      Conversation,
      { conversationId: string; updates: UpdateConversationDTO }
    >({
      query: ({ conversationId, updates }) => ({
        url: `/api/conversations/${conversationId}/title`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    deleteConversation: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/api/conversations/${conversationId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    getMessages: builder.query<DexibitUIMessage[], { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/messages`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "Messages", id: conversationId },
      ],
    }),

    createMessage: builder.mutation<
      DexibitUIMessage,
      { conversationId: string; message: Partial<DexibitUIMessage> }
    >({
      query: ({ conversationId, message }) => ({
        url: `/api/conversations/${conversationId}/messages`,
        method: "POST",
        body: message,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Messages", id: conversationId },
        { type: "Conversations", id: conversationId },
        { type: "ConversationSummary", id: conversationId },
      ],
    }),

    generateConversationTitle: builder.mutation<
      Conversation,
      { conversationId: string; initialMessage: string }
    >({
      query: ({ conversationId, initialMessage }) => ({
        url: `/api/conversations/${conversationId}/title/generate`,
        method: "POST",
        body: { initialMessage },
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
        { type: "Conversations", id: "LIST" },
      ],
    }),

    getConversationAccess: builder.query<AccessResponse, { conversationId: string }>({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/access`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationAccess", id: conversationId },
      ],
    }),

    updateConversationAccess: builder.mutation<
      void,
      { conversationId: string; request: BulkAccessRequest }
    >({
      query: ({ conversationId, request }) => ({
        url: `/api/conversations/${conversationId}/access`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationAccess", id: conversationId },
      ],
    }),

    submitMessageFeedback: builder.mutation<
      MessageFeedbackResponse,
      MessageFeedbackRequest
    >({
      query: ({ messageId, value, comment }) => ({
        url: `/api/messages/${messageId}/feedback`,
        method: "POST",
        body: { value, ...(comment && { comment }) },
      }),
    }),

    forkConversation: builder.mutation<
      Conversation,
      { conversationId: string; data: ForkConversationDTO }
    >({
      query: ({ conversationId, data }) => ({
        url: `/api/conversations/${conversationId}/fork`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    compactConversation: builder.mutation<
      Conversation,
      { conversationId: string; data: CompactConversationDTO }
    >({
      query: ({ conversationId, data }) => ({
        url: `/api/conversations/${conversationId}/compact`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),

    cancelChatStream: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/api/chat/${conversationId}/stream`,
        method: "DELETE",
      }),
    }),

    getConversationSummary: builder.query<
      { summary: string },
      { conversationId: string }
    >({
      query: ({ conversationId }) => `/api/conversations/${conversationId}/summary`,
      providesTags: (_result, _error, { conversationId }) => [
        { type: "ConversationSummary", id: conversationId },
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationByIdQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useUpdateConversationTitleMutation,
  useDeleteConversationMutation,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useCreateMessageMutation,
  useGenerateConversationTitleMutation,
  useGetConversationAccessQuery,
  useUpdateConversationAccessMutation,
  useSubmitMessageFeedbackMutation,
  useForkConversationMutation,
  useCompactConversationMutation,
  useCancelChatStreamMutation,
  useGetConversationSummaryQuery,
  useLazyGetConversationSummaryQuery,
} = conversationService;
